import { useState } from 'react';
import openaiService from '@/services/openai';
import { OpenAiCompletionParams } from '@/types/api';

interface UseOpenAiReturn {
  isLoading: boolean;
  error: Error | null;
  generateCompletion: (
    prompt: string,
    resumeUrl: string,
    options?: { systemMessage?: string }
  ) => Promise<string>;
  generatePreScreeningReview: (
    questions: { questionText: string; audioUrl: string }[]
  ) => Promise<string>;
}

interface UseOpenAiProps {
  systemMessage?: string;
}

export const useOpenAi = ({
  systemMessage = 'You are a helpful assistant.',
}: UseOpenAiProps = {}): UseOpenAiReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const generateCompletion = async (
    prompt: string,
    resumeUrl: string,
    options
  ): Promise<string> => {
    const { systemMessage: customSystemMessage } = options || {};
    setIsLoading(true);
    setError(null);

    try {
      const params: OpenAiCompletionParams = {
        prompt,
        systemMessage: customSystemMessage || systemMessage,
        resumeUrl,
      };

      const response = await openaiService.sendPromptToOpenAi(params);
      return response.data.content || '';
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePreScreeningReview = async (
    questions: { questionText: string; audioUrl: string }[]
  ): Promise<string> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await openaiService.generatePreScreeningReview({ questions });
      return response.data.content || '';
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(new Error(errorMessage));
      return '';
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    generateCompletion,
    generatePreScreeningReview,
  };
};
